import { useRef, useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import about0 from "@/assets/about-0.jpg";
import about1 from "@/assets/about-1.jpg";
import about2 from "@/assets/about-2.jpg";
import about3 from "@/assets/about-3.jpg";
import about4 from "@/assets/about-4.jpg";
import about5 from "@/assets/about-5.jpg";

const photos: string[] = [about0, about2, about3, about1, about5, about4];

const AboutCarousel = () => {
  const { lang } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [parallaxY, setParallaxY] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const y = -rect.top * 0.05;
      setParallaxY(y);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  return (
    <>
      <div ref={containerRef} className="mt-4 pt-4 pb-1 md:mt-10 md:pt-8 md:pb-4 overflow-visible">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto overflow-y-visible pb-1 md:pb-3 snap-x snap-mandatory scrollbar-hide"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {photos.map((photo, index) => {
            return (
              <div
                key={index}
                className="shrink-0 snap-start group cursor-pointer"
                style={{
                  transform: `translateY(${parallaxY * (index % 2 === 0 ? 1 : -0.5)}px)`,
                }}
                onClick={() => setLightbox(index)}
              >
                <div
                  className="h-52 md:h-80 lg:h-96 overflow-hidden rounded-md relative will-change-transform"
                >
                  <img
                    src={photo}
                    alt={lang === "pl" ? `Zdjęcie ${index + 1}` : `Photo ${index + 1}`}
                    className="h-full w-auto object-contain pointer-events-none brightness-75 transition-all duration-500 ease-out group-hover:scale-105 group-hover:brightness-100"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            );
          })}
          <div className="shrink-0 w-6 md:w-12 lg:w-20" />
        </div>

        <div className="mr-20 md:mr-12 lg:mr-20 mt-2 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] font-mono text-muted-foreground/40 tracking-[0.3em] uppercase">
            {lang === "pl" ? "przewiń" : "scroll"} →
          </span>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-md animate-fade-in cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <img
            src={photos[lightbox]}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-foreground/60 hover:text-foreground text-3xl font-light transition-colors"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default AboutCarousel;
